import * as React from 'react';
import styled from 'styled-components';
import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import type { GenerateReportStatus } from '../types';
import { getReportCopy } from '../lib/reportCopy';
import { trackReportEvent } from '../lib/analytics';
import { isSafeReportDownloadUrl } from '../lib/urlSafety';

const Banner = styled.section<{
  $intent: 'info' | 'success' | 'warning' | 'error';
}>`
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  background-color: ${(props) => {
    switch (props.$intent) {
      case 'success':
        return 'var(--brand-50)';
      case 'warning':
        return 'var(--warning-50)';
      case 'error':
        return 'var(--danger-50)';
      default:
        return 'var(--surface-strong)';
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$intent) {
        case 'success':
          return 'var(--brand-200)';
        case 'warning':
          return 'var(--warning-100)';
        case 'error':
          return 'var(--danger-100)';
        default:
          return 'var(--border-strong)';
      }
    }};
  box-shadow: var(--shadow-sm);
  position: relative;
`;

const BannerHeading = styled.h2`
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const NewTabHint = styled.small`
  color: var(--text-muted);
`;

const BannerBody = styled.div`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const BannerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.875rem;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--text-secondary);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-600);
    outline-offset: 2px;
  }
`;

const LiveRegion = styled.div`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

export interface DownloadReportBannerProps {
  status: GenerateReportStatus;
  downloadUrl: string | null;
  expiresAt?: string | null;
  jobId?: string | null;
  locale?: string;
  restaurantId?: number | string | null;
  onDismiss?: () => void;
  onRetry?: () => void;
  isRetrying?: boolean;
}

function getIntent(
  status: GenerateReportStatus,
): 'info' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'ready':
      return 'success';
    case 'failed':
      return 'error';
    case 'pending':
    case 'processing':
    case 'submitting':
      return 'info';
    default:
      return 'info';
  }
}

function formatExpiry(
  expiresAt: string | null | undefined,
  locale: string,
): string | null {
  if (!expiresAt) {
    return null;
  }

  const timestamp = new Date(expiresAt);
  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(timestamp);
}

export const DownloadReportBanner: React.FC<DownloadReportBannerProps> = ({
  status,
  downloadUrl,
  expiresAt,
  jobId,
  locale = 'es-ES',
  restaurantId = null,
  onDismiss,
  onRetry,
  isRetrying = false,
}) => {
  const copy = React.useMemo(() => getReportCopy(locale), [locale]);
  const [, setExpiryCheck] = React.useState(0);
  const isExpired = (() => {
    if (!expiresAt) return false;
    const expiry = new Date(expiresAt);
    return !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
  })();
  React.useEffect(() => {
    if (!expiresAt) return undefined;
    const expiry = new Date(expiresAt).getTime();
    if (Number.isNaN(expiry) || expiry <= Date.now()) return undefined;

    const timeoutId = window.setTimeout(
      () => setExpiryCheck((current) => current + 1),
      Math.min(expiry - Date.now() + 50, 2_147_483_647),
    );
    return () => window.clearTimeout(timeoutId);
  }, [expiresAt]);
  const rawIntent = getIntent(status);
  const intent =
    isExpired && status === 'ready' ? ('warning' as const) : rawIntent;
  const formattedExpiry = React.useMemo(
    () => formatExpiry(expiresAt, locale),
    [expiresAt, locale],
  );

  const canDownload = status === 'ready' && Boolean(downloadUrl) && !isExpired;
  const canRetry =
    (status === 'failed' || (status === 'ready' && isExpired)) &&
    typeof onRetry === 'function';
  const isDownloadUrlSafe = React.useMemo(
    () => isSafeReportDownloadUrl(downloadUrl),
    [downloadUrl],
  );
  const showDownloadLink = canDownload && isDownloadUrlSafe;
  const showUnsafeMessage =
    canDownload && !isDownloadUrlSafe && Boolean(downloadUrl);

  let heading = copy.banner.pending.title;
  let message = copy.banner.pending.message;

  if (status === 'ready') {
    heading = copy.banner.ready.title;
    message = formattedExpiry
      ? copy.banner.readyWithExpiry.message.replace('{{date}}', formattedExpiry)
      : copy.banner.ready.message;
  } else if (status === 'failed') {
    heading = copy.banner.failed.title;
    message = copy.banner.failed.message;
  } else if (status === 'submitting') {
    heading = copy.banner.submitting.title;
    message = copy.banner.submitting.message;
  } else if (status === 'processing') {
    heading = copy.banner.processing.title;
    message = copy.banner.processing.message;
  }

  if (isExpired && status === 'ready') {
    heading = copy.banner.expired.title;
    message = copy.banner.expired.message;
  }

  if (showUnsafeMessage) {
    heading = copy.banner.unsafe.title;
    message = copy.banner.unsafe.message;
  }

  const liveMessage =
    status === 'ready'
      ? copy.announcements.ready
      : status === 'failed'
        ? copy.announcements.failed
        : '';

  const handleDownloadClick = React.useCallback(() => {
    if (!downloadUrl || !isDownloadUrlSafe) return;

    trackReportEvent('report.download', {
      jobId,
      restaurantId,
      status,
      expiresAt: expiresAt ?? null,
      locale,
    });
  }, [
    downloadUrl,
    expiresAt,
    isDownloadUrlSafe,
    jobId,
    locale,
    restaurantId,
    status,
  ]);

  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const previousStatusRef = React.useRef(status);

  React.useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = status;
    if (
      previousStatus !== status &&
      (status === 'ready' || status === 'failed')
    ) {
      headingRef.current?.focus();
    }
  }, [status]);

  const isBusy =
    status === 'submitting' || status === 'pending' || status === 'processing';

  return (
    <Banner $intent={intent} aria-live="polite" aria-busy={isBusy}>
      <LiveRegion role="status" aria-atomic="true">
        {liveMessage}
      </LiveRegion>

      <BannerHeading ref={headingRef} tabIndex={-1}>
        <Icon
          name={
            intent === 'success'
              ? 'success'
              : intent === 'error'
                ? 'error'
                : 'info'
          }
          size="sm"
          aria-hidden
        />
        {heading}
      </BannerHeading>

      <BannerBody>
        <p>{message}</p>
        {jobId && (
          <small>
            {copy.download.trackingLabel}: <code>{jobId}</code>
          </small>
        )}
        {showUnsafeMessage && (
          <small role="alert">{copy.download.invalid}</small>
        )}
      </BannerBody>

      <BannerActions>
        {showDownloadLink && downloadUrl && (
          <Button
            as="a"
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${copy.download.cta}. ${copy.download.newTabHint}`}
            variant="primary"
            size="sm"
            startIcon={<Icon name="download" aria-hidden />}
            onClick={handleDownloadClick}
          >
            {copy.download.cta}
          </Button>
        )}
        {showDownloadLink && (
          <NewTabHint>{copy.download.newTabHint}</NewTabHint>
        )}

        {canRetry && onRetry && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onRetry}
            loading={isRetrying}
            startIcon={<Icon name="refresh" aria-hidden />}
          >
            {copy.download.retry}
          </Button>
        )}

        {onDismiss && (
          <DismissButton type="button" onClick={onDismiss}>
            {copy.download.dismiss}
          </DismissButton>
        )}
      </BannerActions>
    </Banner>
  );
};
